import { formatCurrency } from '../utils/money'
import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import Navbar from '../components/Navbar'
import { paymentApi } from '../lib/api'

interface PaymentResult {
  status: string
  isPaid: boolean
  amount: number | null
  rentalId: string
  isExtension: boolean
}

export default function CheckoutSuccess() {
  const location = useLocation()
  const [result, setResult] = useState<PaymentResult | null>(null)
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [pollCount, setPollCount] = useState(0)

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const paymentIntent = params.get('payment_intent')
    const clientSecret = params.get('payment_intent_client_secret')
    const redirectStatus = params.get('redirect_status')
    const rentalId = params.get('rental_id')
    const isExtension = params.get('type') === 'extension'

    if (!paymentIntent || !clientSecret) {
      setError('Missing payment information.')
      setIsLoading(false)
      return
    }

    if (redirectStatus === 'succeeded') {
      const targetRentalId = rentalId
      if (targetRentalId) {
        // Poll for webhook to process — it may take a few seconds
        pollPaymentStatus(targetRentalId, 0, isExtension)
      } else {
        setError('Unable to associate payment with a rental.')
        setIsLoading(false)
      }
    } else {
      setError(`Payment did not complete (status: ${redirectStatus}).`)
      setIsLoading(false)
    }
  }, [location.search])

  const pollPaymentStatus = async (rentalId: string, attempt: number, isExtension: boolean) => {
    try {
      setIsLoading(true)
      setPollCount(attempt + 1)
      const response = isExtension ? await paymentApi.getExtensionStatus(rentalId) : await paymentApi.getStatus(rentalId)
      if (response.data.success) {
        const data = response.data.data
        // If webhook has processed, isPaid/extended will be true
        const done = isExtension ? data.extended : data.isPaid
        if (done) {
          setResult({
            rentalId,
            status: data.status,
            isPaid: true,
            amount: data.amount,
            isExtension,
          })
          setIsLoading(false)
          return
        }
        // Webhook hasn't processed yet — retry up to 5 times (5s apart)
        if (attempt < 5) {
          setTimeout(() => pollPaymentStatus(rentalId, attempt + 1, isExtension), 2000)
          return
        }
        // Fallback after retries: show success based on Stripe status
        setResult({
          rentalId,
          status: data.status,
          isPaid: data.status === 'succeeded',
          amount: data.amount,
          isExtension,
        })
        setIsLoading(false)
      } else {
        setError(response.data.error || 'Could not verify payment status.')
        setIsLoading(false)
      }
    } catch (err: any) {
      // A 404 right after paying can be a webhook/poll race; retry briefly before giving up.
      if (err?.response?.status === 404 && attempt < 3) {
        setTimeout(() => pollPaymentStatus(rentalId, attempt + 1, isExtension), 2000)
        return
      }
      setError(err?.response?.data?.error || 'Could not verify payment status.')
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex flex-col bg-[var(--bg-primary)]">
      <Navbar />
      <main className="min-h-[calc(100vh-var(--navbar-height))]">
        <div className="container mx-auto px-4 py-16 text-center">
          <div className="bg-white rounded-2xl p-12 max-w-lg mx-auto">
            {isLoading && (
              <>
                <div className="animate-spin h-12 w-12 border-4 border-[var(--bg-tint)] border-t-[var(--forest-green)] rounded-full mx-auto mb-4"></div>
                <div className="text-[var(--text-secondary)]">
                  {pollCount > 1 ? `Confirming payment (attempt ${pollCount})...` : 'Verifying payment...'}
                </div>
              </>
            )}

            {!isLoading && error && (
              <>
                <div className="text-6xl mb-4">⚠️</div>
                <h1 className="text-2xl font-bold mb-4">Payment Issue</h1>
                <p className="text-[var(--text-secondary)] mb-6">{error}</p>
                <Link to="/my-rentals" className="btn-primary w-full py-3 rounded-lg block">
                  My Rentals
                </Link>
              </>
            )}

            {!isLoading && result && (
              <>
                <div className="text-6xl mb-4">🎉</div>
                <h1 className="text-2xl font-bold mb-4">{result.isExtension ? 'Extension Confirmed' : 'Payment Complete'}</h1>
                <p className="text-[var(--text-secondary)] mb-2">
                  Status: {result.status}
                </p>
                {result.amount != null && (
                  <p className="text-[var(--text-secondary)] mb-6">
                    Amount: {formatCurrency(result.amount)}
                  </p>
                )}
                <div className="space-y-3">
                  <Link
                    to={`/rental/${result.rentalId}`}
                    className="btn-primary w-full py-3 rounded-lg block"
                  >
                    View Rental Details
                  </Link>
                  <Link to="/my-rentals" className="text-[var(--forest-green)] block">
                    My Rentals
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  )
}